import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import {
  formatBytes,
  getAttachmentMediaType,
  getAttachmentMediaUrl,
  getAttachmentPreviewUrl,
  getAttachmentVideoMimeType,
} from './helpers';

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 5;
const ZOOM_STEP = 0.25;

const AttachmentPreviewModal = ({ attachment, open, onClose }) => {
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    if (open) {
      setZoom(1);
    }
  }, [open, attachment?.id]);

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  const mediaType = useMemo(() => (attachment ? getAttachmentMediaType(attachment) : 'file'), [attachment]);
  const mediaUrl = useMemo(() => (attachment ? getAttachmentMediaUrl(attachment) : null), [attachment]);
  const previewUrl = useMemo(() => (attachment ? getAttachmentPreviewUrl(attachment) : null), [attachment]);
  const videoMimeType = useMemo(
    () => (attachment && mediaType === 'video' ? getAttachmentVideoMimeType(attachment) : undefined),
    [attachment, mediaType]
  );

  if (!open || !attachment) {
    return null;
  }

  const portalTarget = typeof document !== 'undefined' ? document.body : null;
  if (!portalTarget) {
    return null;
  }

  const handleZoom = (delta) => {
    setZoom((prev) => {
      const next = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, prev + delta));
      return Number(next.toFixed(2));
    });
  };

  const resetZoom = () => setZoom(1);

  const renderMedia = () => {
    if (!mediaUrl && !previewUrl) {
      return (
        <div className="flex items-center justify-center h-64 text-gray-500">
          No preview available for this attachment
        </div>
      );
    }

    if (mediaType === 'video') {
      return (
        <video
          key={attachment.id}
          controls
          playsInline
          preload="metadata"
          poster={previewUrl || undefined}
          className="w-full max-h-[70vh] rounded bg-black"
        >
          {mediaUrl && (
            <source
              src={mediaUrl}
              type={videoMimeType}
            />
          )}
          Your browser does not support the video tag.
        </video>
      );
    }

    if (mediaType === 'audio') {
      return (
        <div className="flex flex-col items-center gap-4">
          <audio key={attachment.id} controls className="w-full" src={mediaUrl}>
            Your browser does not support the audio element.
          </audio>
          {previewUrl && (
            <img src={previewUrl} alt={attachment.title} className="rounded max-h-64" loading="lazy" />
          )}
        </div>
      );
    }

    if (mediaType === 'image') {
      return (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <button type="button" className="btn btn-default btn-xs" onClick={() => handleZoom(-ZOOM_STEP)} disabled={zoom <= ZOOM_MIN}>
              -
            </button>
            <input
              type="range"
              min={ZOOM_MIN}
              max={ZOOM_MAX}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1"
            />
            <button type="button" className="btn btn-default btn-xs" onClick={() => handleZoom(ZOOM_STEP)} disabled={zoom >= ZOOM_MAX}>
              +
            </button>
            <button type="button" className="btn btn-link btn-xs" onClick={resetZoom}>
              Reset
            </button>
            <div className="text-xs text-gray-500 min-w-[60px] text-right">{Math.round(zoom * 100)}%</div>
          </div>
          <div className="relative max-h-[70vh] overflow-auto rounded border border-gray-200 bg-gray-50">
            <img
              src={mediaUrl || previewUrl}
              alt={attachment.title}
              className="block select-none"
              style={{ transform: `scale(${zoom})`, transformOrigin: 'top left' }}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="text-sm text-gray-600">
        <p>This file type cannot be previewed, but you can download it below.</p>
      </div>
    );
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative z-10 flex w-full max-w-5xl max-h-[92vh] flex-col overflow-hidden rounded-lg bg-white shadow-2xl">
        <div className="flex items-start justify-between px-4 py-3 border-b">
          <div>
            <div className="text-base font-semibold">{attachment.title || `Attachment #${attachment.id}`}</div>
            <div className="text-xs text-gray-500">
              {attachment.mime_type || 'Unknown type'} • {formatBytes(attachment.size_bytes)}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {mediaUrl && (
              <a href={mediaUrl} target="_blank" rel="noopener noreferrer" className="btn btn-default btn-xs">
                Open original
              </a>
            )}
            <button type="button" className="btn btn-link btn-xs" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
        <div className="flex-1 p-4 overflow-auto">
          {renderMedia()}
        </div>
      </div>
    </div>,
    portalTarget
  );
};

AttachmentPreviewModal.propTypes = {
  attachment: PropTypes.object,
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};

AttachmentPreviewModal.defaultProps = {
  attachment: null,
  open: false,
};

export default AttachmentPreviewModal;
